import { createElement, Fragment, type ReactNode, useMemo } from 'react';
import { parseMarkedText, type TextMarker } from '@/shared/lib';
import { sanitizeRichTextHtml } from '@/shared/ui';

type Props<T> = {
  /** Комментарий с разметкой */
  html: string;
  markers: TextMarker<T>[];
  renderMarker: (marker: T, index: number) => ReactNode;
  /**
   * Рендер текста между тегами; `offset` — позиция этого текста в тексте всего комментария
   * без разметки. Без него текст рисуется обычным, с распознанными маркерами.
   */
  renderText?: (text: string, offset: number) => ReactNode;
};

/**
 * Комментарий с HTML-разметкой: теги отрисовываются, маркеры внутри текста остаются кликабельными.
 *
 * Разметку санитизирует `sanitizeRichTextHtml` — тем же набором тегов, что разрешает редактор,
 * поэтому сохранённое оформление не пропадает при чтении. Затем дерево переносится в React-элементы — так маркеры
 * (таймкоды, ссылки на правила и узлы знаний) остаются интерактивными,
 * чего не даёт `dangerouslySetInnerHTML`.
 */
export const HtmlCommentContent = <T,>({ html, markers, renderMarker, renderText }: Props<T>) => {
  const elements = useMemo(() => {
    /** Текстовая нода: обычные фрагменты вперемешку с маркерами */
    const renderMarkedText = (text: string): ReactNode[] =>
      parseMarkedText(text, markers).map((part, index) =>
        part.kind === 'text' ? <Fragment key={index}>{part.text}</Fragment> : renderMarker(part.marker, index),
      );

    // Разметка позиции не занимает: потребителю нужен отсчёт по видимому тексту
    let textOffset = 0;

    const domNodeToReact = (node: ChildNode, index: number): ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        const offset = textOffset;
        textOffset += text.length;
        return <Fragment key={index}>{renderText ? renderText(text, offset) : renderMarkedText(text)}</Fragment>;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tag = element.tagName.toLowerCase();
        if (tag === 'br') return <br key={index} />;

        const children = Array.from(element.childNodes).map((child, childIndex) => domNodeToReact(child, childIndex));
        return createElement(tag, { key: index }, ...children);
      }

      return null;
    };

    const doc = new DOMParser().parseFromString(`<div>${sanitizeRichTextHtml(html)}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return null;

    return Array.from(root.childNodes).map((node, index) => domNodeToReact(node, index));
  }, [html, markers, renderMarker, renderText]);

  return <div className="[&_ol]:list-decimal [&_ul]:list-disc [&_li]:ml-4 [&_ol]:ml-2 [&_ul]:ml-2">{elements}</div>;
};
